const PERCENTAGE_BASE = 100;
const MIN_PERCENTAGE = 0;
const MAX_PERCENTAGE = 100;

export type ResourceUsage = {
  readonly used: number;
  readonly total: number;
};

export type ResourceStats = {
  readonly used: number;
  readonly total: number;
  readonly available: number;
  readonly percentageUsed: number;
};

/**
 * Calculate percentage used with bounds checking
 */
const calculatePercentage = (used: number, total: number): number => {
  if (total <= 0) return MIN_PERCENTAGE;

  const rawPercentage = (used / total) * PERCENTAGE_BASE;
  return Math.min(MAX_PERCENTAGE, Math.max(MIN_PERCENTAGE, rawPercentage));
};

/**
 * Calculate available resources with overflow protection
 */
const calculateAvailable = (total: number, used: number): number => {
  return Math.max(0, total - used);
};

/**
 * Calculate resource statistics
 *
 * @param resource - Resource usage data
 * @returns Complete resource statistics
 */
export const calculateResourceStats = (resource: ResourceUsage): ResourceStats => {
  return {
    used: resource.used,
    total: resource.total,
    available: calculateAvailable(resource.total, resource.used),
    percentageUsed: calculatePercentage(resource.used, resource.total),
  };
};

/**
 * Format percentage for display
 */
export const formatPercentage = (percentage: number, decimalPlaces: number = 1): string => {
  return `${percentage.toFixed(decimalPlaces)}%`;
};

/**
 * Check if resource is critically low
 */
export const isResourceCritical = (stats: ResourceStats, threshold: number = 90): boolean => {
  return stats.percentageUsed >= threshold;
};

/**
 * Check if resource is running low
 */
export const isResourceLow = (stats: ResourceStats, threshold: number = 75): boolean => {
  return stats.percentageUsed >= threshold && stats.percentageUsed < 90;
};
